import React, { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { toast } from "react-hot-toast";
import { API_ENDPOINTS } from "../config/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import "../App.css";

const PLATE_STYLES = {
  "malaysia-new": {
    containerClasses:
      "relative px-1 bg-gray-50 rounded-sm border border-gray-200 shadow-sm flex-shrink-0",
    stripClasses:
      "absolute top-0 left-0 right-0 w-[4px] h-[100%] bg-lime-500 rounded-tl-[0.2em] rounded-bl-[0.2em]",
    textClasses:
      "font-bold text-gray-900 tracking-widest ml-1 font-['Bebas_Neue'] dark:text-gray-900",
  },
  "malaysia-legacy": {
    containerClasses:
      "relative px-1 bg-[#222222] rounded-sm border-black shadow-sm flex-shrink-0",
    stripClasses: "hidden",
    textClasses: "text-white tracking-wider font-arial",
  },
  "malaysia-cursive": {
    containerClasses:
      "relative px-1 bg-[#222222] rounded-sm border-black shadow-sm flex-shrink-0",
    stripClasses: "hidden",
    textClasses: "text-white tracking-wider font-['Georgia'] italic",
  },
  "malaysia-taxi": {
    containerClasses:
      "relative px-1 bg-white rounded-sm shadow-sm flex-shrink-0",
    stripClasses: "hidden",
    textClasses:
      "font-medium text-black tracking-wider font-arial dark:text-black",
  },
  "malaysia-diplomat": {
    containerClasses:
      "relative px-1 bg-red-600 rounded-sm border border-red-700 shadow-sm flex-shrink-0",
    stripClasses: "hidden",
    textClasses: "font-bold text-white tracking-wider font-arial",
  },
  thai: {
    containerClasses:
      "relative px-1 bg-white rounded-sm border-2 border-black shadow-sm flex-shrink-0",
    stripClasses: "hidden",
    textClasses: "font-bold text-black tracking-widest dark:text-black",
  },
  singapore: {
    containerClasses:
      "relative px-1 bg-[#222222] rounded-sm border border-gray-200 shadow-sm flex-shrink-0",
    stripClasses: "hidden",
    textClasses: "font-['Quantico'] font-bold text-white tracking-wider",
  },
};

const thaiAlphabetReplacement = {
  A: "ก",
  B: "ข",
  C: "ค",
  D: "ง",
  E: "จ",
  F: "ฉ",
  G: "ช",
  H: "ฌ",
  I: "ฏ",
  J: "ฐ",
  K: "ณ",
  L: "ด",
  M: "ต",
  N: "ถ",
  O: "ท",
  P: "ธ",
  Q: "น",
  R: "บ",
  S: "ป",
  T: "ผ",
  U: "ฝ",
  V: "พ",
  W: "ฟ",
  X: "ภ",
  Y: "ม",
  Z: "ย",
};

// Function to capitalize first letter of each word for cursive style
const capitalizePlate = (plate) => {
  // For plates like "PUTRAJAYA" -> "Putrajaya"
  if (plate && plate.length > 2) {
    return plate
      .split(" ")
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(" ");
  }
  return plate;
};

const PlateDisplay = ({
  plate,
  plateStyle = "malaysia-legacy",
  size = "default",
  isEditable = false,
  canEditPlate = false,
  vehicleId = null,
  onStyleChange = null,
  onPlateChange = null,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(plateStyle);
  const [previewStyle, setPreviewStyle] = useState(plateStyle);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPlateEditOpen, setIsPlateEditOpen] = useState(false);
  const [newPlate, setNewPlate] = useState(plate);
  const [isPlateUpdating, setIsPlateUpdating] = useState(false);

  useEffect(() => {
    setSelectedStyle(plateStyle);
    setPreviewStyle(plateStyle);
    setNewPlate(plate);
  }, [plateStyle, plate]);

  const sizeClasses = {
    small: "text-xs",
    default: "text-base",
    large: "text-lg",
    logo: "text-3xl",
  };

  const style = PLATE_STYLES[selectedStyle] || PLATE_STYLES["malaysia-legacy"];

  const handleStyleChange = async () => {
    if (!vehicleId || !onStyleChange) return;

    const previousStyle = selectedStyle;
    const newStyle = previewStyle;

    setSelectedStyle(newStyle);
    setIsOpen(false);
    setIsUpdating(true);

    try {
      const response = await fetch(
        API_ENDPOINTS.UPDATE_VEHICLE_PLATE_STYLE(vehicleId),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ plateStyle: newStyle }),
        }
      );

      if (!response.ok) throw new Error("Failed to update plate style");

      const data = await response.json();
      onStyleChange({ ...data, plateStyle: newStyle });
      toast.success("Plate style updated!");
    } catch (error) {
      console.error("Error updating plate style:", error);
      setSelectedStyle(previousStyle);
      setPreviewStyle(previousStyle);
      toast.error("Failed to update plate style");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setPreviewStyle(selectedStyle);
    setIsOpen(false);
  };

  const handlePlateChange = async () => {
    if (!vehicleId || !onPlateChange) return;

    const previousPlate = plate;
    const updatedPlate = newPlate;

    setIsPlateEditOpen(false);
    setIsPlateUpdating(true);

    try {
      const response = await fetch(
        API_ENDPOINTS.UPDATE_VEHICLE_PLATE(vehicleId),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ plate: updatedPlate }),
        }
      );

      let errorData;
      try {
        // First try to parse the response as JSON
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};

        if (!response.ok) {
          errorData = data;
          throw new Error(data.msg || "Failed to update plate number");
        }

        // Success case
        onPlateChange(data);
        toast.success("Plate number updated!");

        // Redirect to the new plate URL
        window.location.href = `/plate/${data.plate}`;
      } catch (parseError) {
        console.error("Error parsing response:", parseError);
        throw new Error("Server returned invalid response. Please try again.");
      }
    } catch (error) {
      console.error("Error updating plate number:", error);
      setNewPlate(previousPlate);
      toast.error(error.message || "Failed to update plate number");
    } finally {
      setIsPlateUpdating(false);
    }
  };

  const handleClosePlateEdit = () => {
    setNewPlate(plate);
    setIsPlateEditOpen(false);
  };

  const plateDisplay = (
    <div
      className={`${style.containerClasses} ${sizeClasses[size]} ${className} ${
        isEditable
          ? "cursor-pointer hover:scale-105 transition-all plate-bounce"
          : ""
      }`}
      onClick={isEditable ? () => setIsOpen(true) : undefined}
    >
      <div className={style.stripClasses} />
      <span className={style.textClasses}>
        {selectedStyle === "thai"
          ? plate
              .split("")
              .map((char) => thaiAlphabetReplacement[char] || char)
              .join("")
          : selectedStyle === "malaysia-cursive"
          ? capitalizePlate(plate)
          : plate}
      </span>
      <style>{`
        @keyframes gentle-bounce {
          0%,
          20%,
          50%,
          80%,
          100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-3px);
          }
          60% {
            transform: translateY(-2px);
          }
        }
        .plate-bounce {
          animation: gentle-bounce 3s infinite;
        }
      `}</style>
    </div>
  );

  if (!isEditable && !canEditPlate) return plateDisplay;

  return (
    <>
      <div className="flex items-center">
        {plateDisplay}
        {canEditPlate && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsPlateEditOpen(true);
            }}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors ml-1"
            title="Edit plate number"
          >
            <FontAwesomeIcon icon={faPencilAlt} className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium mb-2">
              Select Plate Style
            </Dialog.Title>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-2">
                Current Style: {selectedStyle}
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Preview Style: {previewStyle}
              </div>
              <div className="flex justify-center">
                <div className={PLATE_STYLES[previewStyle].containerClasses}>
                  <div className={PLATE_STYLES[previewStyle].stripClasses} />
                  <span className={PLATE_STYLES[previewStyle].textClasses}>
                    {previewStyle === "thai"
                      ? plate
                          .split("")
                          .map((char) => thaiAlphabetReplacement[char] || char)
                          .join("")
                      : previewStyle === "malaysia-cursive"
                      ? capitalizePlate(plate)
                      : plate}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {Object.entries(PLATE_STYLES).map(([styleKey, styleConfig]) => (
                <button
                  key={styleKey}
                  className={`w-full p-4 rounded-lg border ${
                    previewStyle === styleKey
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setPreviewStyle(styleKey)}
                  disabled={isUpdating}
                >
                  <div className={styleConfig.containerClasses}>
                    <div className={styleConfig.stripClasses} />
                    <span className={styleConfig.textClasses}>
                      {styleKey === "thai"
                        ? plate
                            .split("")
                            .map(
                              (char) => thaiAlphabetReplacement[char] || char
                            )
                            .join("")
                        : styleKey === "malaysia-cursive"
                        ? capitalizePlate(plate)
                        : plate}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleStyleChange}
                disabled={isUpdating || previewStyle === selectedStyle}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  isUpdating || previewStyle === selectedStyle
                    ? "bg-blue-300"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isUpdating ? "Updating..." : "Confirm Style"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={isPlateEditOpen}
        onClose={handleClosePlateEdit}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium mb-2">
              Edit Plate Number
            </Dialog.Title>

            <div className="mb-4">
              <label
                htmlFor="plate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Plate Number
              </label>
              <input
                type="text"
                id="plate"
                value={newPlate}
                onChange={(e) => setNewPlate(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter plate number"
                maxLength={10}
              />
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleClosePlateEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handlePlateChange}
                disabled={isPlateUpdating || newPlate === plate}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  isPlateUpdating || newPlate === plate
                    ? "bg-blue-300"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isPlateUpdating ? "Updating..." : "Confirm Plate"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
};

export default PlateDisplay;
